/**
 * Состояние боя для рендера и интерфейса: снимок {@link BattleSnapshot} на каждый тик.
 *
 * Снимок — только чтение: engine (или заглушка `createFakeBattleStream`) отдаёт новый объект каждый тик, рендер (C1),
 * HUD (C3), окна (C4) и звук (C7) ничего в нём не меняют. Всё, что произошло за тик (выстрел, смерть, монеты), —
 * в {@link BattleSnapshot.events}: звук и аналитика слушают события, а не сравнивают снимки.
 *
 * Сущности с `id` живут, пока есть в массиве: исчез из `zombies` — рендер убирает спрайт (анимацию смерти engine
 * показывает сам, держа зомби с `anim: "die"` до конца анимации). Идентификаторы в пределах волны не повторяются.
 */
import { z } from "zod";
import { ProjectileKind } from "./balance";
import {
  Coins,
  ContentId,
  Direction,
  EntityId,
  FIXED_ONE,
  HeroId,
  PlatformSlot,
  Permille,
  PosInt,
  Tick,
  Ticks,
  UInt,
  Vec2,
  ZombieKind,
} from "./common";

/** Размер поля, единиц фиксированной точки. Поле — прямоугольник `x ∈ [−width/2, width/2]`, `y ∈ [0, depth]`. */
export const FieldGeometry = z.object({
  width: PosInt,
  depth: PosInt,
});
export type FieldGeometry = z.infer<typeof FieldGeometry>;

/** Поле v1 по умолчанию: 9 × 16 клеток, штаб в центре. Engine может переопределить в данных. */
export const DEFAULT_FIELD: FieldGeometry = { width: 9 * FIXED_ONE, depth: 16 * FIXED_ONE };

/**
 * Фаза волны:
 * - `intro` — отсчёт перед волной (зомби ещё не идут);
 * - `fight` — бой;
 * - `cleared` — все зомби волны убиты, сейчас покажется выбор усиления;
 * - `lost` — штаб разрушен, откат на волну назад.
 */
export const WavePhase = z.enum(["intro", "fight", "cleared", "lost"]);
export type WavePhase = z.infer<typeof WavePhase>;

/** Полоса волны и босса в HUD. */
export const WaveState = z.object({
  /** Номер волны, с 1. */
  number: PosInt,
  /** Волна с боссом (каждая 5-я). */
  isBoss: z.boolean(),
  phase: WavePhase,
  /** Тиков с начала волны. */
  tick: Tick,
  /** Всего зомби в волне (вместе с боссом). */
  total: UInt,
  /** Уже появились. */
  spawned: UInt,
  /** Уже убиты. */
  killed: UInt,
  /** Прогресс полосы волны, промилле (`killed / total`). */
  progress: Permille,
  /** Бета: уровень, подволна и локация фона (нет — обычный режим). */
  level: z
    .object({
      level: PosInt,
      subwave: PosInt,
      subwaves: PosInt,
      location: z.string(),
      isBossLevel: z.boolean(),
    })
    .optional(),
  /** Босс на поле — для полосы его прочности. */
  boss: z
    .object({
      entityId: EntityId,
      bossId: ContentId,
      hp: UInt,
      hpMax: PosInt,
    })
    .nullable(),
});
export type WaveState = z.infer<typeof WaveState>;

/** Визуальное состояние штаба (три модели A2 + разрушен). */
export const HqLook = z.enum(["intact", "damaged", "critical", "destroyed"]);
export type HqLook = z.infer<typeof HqLook>;

/** Штаб и его пушка. */
export const HqState = z.object({
  pos: Vec2,
  hp: UInt,
  hpMax: PosInt,
  look: HqLook,
  cannon: z.object({
    facing: Direction,
    /** Выстрел на этом тике (вспышка, отдача). */
    firing: z.boolean(),
    targetId: EntityId.nullable(),
  }),
});
export type HqState = z.infer<typeof HqState>;

/** Платформа героя. */
export const PlatformState = z.object({
  slot: PlatformSlot,
  pos: Vec2,
  /** Открыта (на ней может стоять герой). */
  unlocked: z.boolean(),
  /** Волна открытия — для подписи на закрытой платформе. */
  unlockWave: PosInt,
  /** Кто стоит; `null` — пусто. */
  heroId: HeroId.nullable(),
});
export type PlatformState = z.infer<typeof PlatformState>;

/** Анимация героя (A3: покой, стрельба, навык). */
export const HeroAnim = z.enum(["idle", "shoot", "skill"]);
export type HeroAnim = z.infer<typeof HeroAnim>;

/** Навык героя в ряду кнопок HUD. */
export const SkillState = z.object({
  skillId: ContentId,
  /** Осталось до готовности, тиков (0 — готов). */
  cooldownLeft: Ticks,
  cooldownTotal: Ticks,
  /** Навык сейчас действует (осталось тиков; 0 — не действует). */
  activeLeft: Ticks,
});
export type SkillState = z.infer<typeof SkillState>;

/** Герой на платформе. */
export const HeroState = z.object({
  id: EntityId,
  heroId: HeroId,
  slot: PlatformSlot,
  pos: Vec2,
  facing: Direction,
  anim: HeroAnim,
  /** Тиков с начала текущей анимации — кадр считает рендер по fps из манифеста. */
  animTick: Tick,
  level: PosInt,
  stars: z.number().int().min(0).max(5),
  targetId: EntityId.nullable(),
  skill: SkillState,
});
export type HeroState = z.infer<typeof HeroState>;

/** Анимация зомби (A4: ходьба, атака, смерть — рассыпается кубиками). */
export const ZombieAnim = z.enum(["walk", "attack", "die"]);
export type ZombieAnim = z.infer<typeof ZombieAnim>;

/** Статусы на зомби — для оттенка спрайта и эффектов. */
export const StatusKind = z.enum(["burning", "frozen", "slowed", "shocked"]);
export type StatusKind = z.infer<typeof StatusKind>;

/** Зомби или босс. */
export const ZombieState = z.object({
  id: EntityId,
  /** Вид: обычный зомби или `boss` (тогда задан {@link ZombieState.bossId}). */
  kind: z.union([ZombieKind, z.literal("boss")]),
  bossId: ContentId.nullable(),
  pos: Vec2,
  facing: Direction,
  anim: ZombieAnim,
  animTick: Tick,
  hp: UInt,
  hpMax: PosInt,
  /** Остаток щита (0 — нет). */
  shield: UInt,
  statuses: z.array(StatusKind),
});
export type ZombieState = z.infer<typeof ZombieState>;

/** Снаряд в полёте. */
export const ProjectileState = z.object({
  id: EntityId,
  kind: ProjectileKind,
  pos: Vec2,
  /** Откуда вылетел и куда летит — для поворота спрайта и трассера. */
  from: Vec2,
  to: Vec2,
  /** Стрелок: герой, `hq` — пушка штаба. */
  owner: z.union([HeroId, z.literal("hq")]),
});
export type ProjectileState = z.infer<typeof ProjectileState>;

/** Визуальный эффект (A5). */
export const EffectKind = z.enum([
  "muzzle",
  "hit",
  "explosion",
  "fire",
  "lightning",
  "ice",
  "coinBurst",
  "damageNumber",
  "critNumber",
  "hqHit",
]);
export type EffectKind = z.infer<typeof EffectKind>;

/** Эффект на поле: рисуется с `startTick` в течение `durationTicks`. */
export const EffectState = z.object({
  id: EntityId,
  kind: EffectKind,
  pos: Vec2,
  /** Конечная точка (молния, трассер); `null` — точечный эффект. */
  to: Vec2.nullable(),
  startTick: Tick,
  durationTicks: Ticks,
  /** Число на экране (урон) или радиус взрыва, единиц; 0 — нет. */
  value: UInt,
});
export type EffectState = z.infer<typeof EffectState>;

/** Монета, летящая от убитого зомби к счётчику. */
export const CoinDropState = z.object({
  id: EntityId,
  pos: Vec2,
  value: Coins,
  startTick: Tick,
  /** Через сколько тиков после `startTick` монета долетит и прибавится в `wallet`. */
  flightTicks: Ticks,
});
export type CoinDropState = z.infer<typeof CoinDropState>;

/**
 * Посылка на поле. Точка в промилле от свободной части экрана (`spot`): от неё зависит только рисунок, поэтому клиент
 * сам ставит посылку мимо кнопок HUD на любом экране.
 */
export const ParcelState = z.object({
  id: EntityId,
  spot: z.object({ x: Permille, y: Permille }),
  /** Кадр появления. */
  startTick: Tick,
  /** Последний кадр, когда сервер примет тап. Клиент прячет посылку раньше: по {@link ParcelsDef.lifeTicks} и скорости. */
  endTick: Tick,
});
export type ParcelState = z.infer<typeof ParcelState>;

/** Что дала открытая посылка. */
export const ParcelOpened = z.object({
  id: EntityId,
  reward: z.enum(["coins", "boost"]),
  /** Монеты с `coinGain`, для показа. */
  coins: Coins,
  boostId: ContentId.nullable(),
});
export type ParcelOpened = z.infer<typeof ParcelOpened>;

/** Событие тика. Дискриминант — `type`. Новые типы добавляются только MR в contracts. */
export const BattleEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("waveStarted"), wave: PosInt, isBoss: z.boolean() }),
  z.object({ type: z.literal("zombieSpawned"), id: EntityId, kind: z.union([ZombieKind, z.literal("boss")]) }),
  z.object({ type: z.literal("bossSpawned"), id: EntityId, bossId: ContentId }),
  z.object({ type: z.literal("shot"), owner: z.union([HeroId, z.literal("hq")]), projectileId: EntityId }),
  z.object({ type: z.literal("hit"), targetId: EntityId, damage: PosInt, crit: z.boolean() }),
  z.object({ type: z.literal("zombieKilled"), id: EntityId, kind: z.union([ZombieKind, z.literal("boss")]) }),
  z.object({ type: z.literal("bossKilled"), id: EntityId, bossId: ContentId }),
  z.object({ type: z.literal("coinsDropped"), coinId: EntityId, value: Coins }),
  z.object({ type: z.literal("coinsCollected"), value: Coins }),
  z.object({ type: z.literal("hqDamaged"), damage: PosInt, hp: UInt }),
  z.object({ type: z.literal("skillCast"), heroId: HeroId, skillId: ContentId, auto: z.boolean() }),
  z.object({ type: z.literal("skillReady"), heroId: HeroId }),
  z.object({ type: z.literal("heroUnlocked"), heroId: HeroId, slot: PlatformSlot }),
  z.object({ type: z.literal("parcelDropped"), id: EntityId }),
  z.object({ type: z.literal("parcelOpened"), ...ParcelOpened.shape }),
  /** Режим «Карта»: платформа атакующего получила урон / разрушена (герой на ней выбыл). */
  z.object({ type: z.literal("platformDamaged"), slot: PlatformSlot, damage: PosInt, hp: UInt }),
  z.object({ type: z.literal("platformDestroyed"), slot: PlatformSlot }),
  z.object({ type: z.literal("waveCleared"), wave: PosInt }),
  z.object({ type: z.literal("waveLost"), wave: PosInt, rollbackTo: PosInt }),
]);
export type BattleEvent = z.infer<typeof BattleEvent>;
export type BattleEventType = BattleEvent["type"];

/** Герой-защитник участка (копия героя владельца под ИИ) в бою атаки. */
export const DefenderState = z.object({
  id: EntityId,
  heroId: HeroId,
  slot: PlatformSlot,
  pos: Vec2,
  facing: Direction,
  anim: HeroAnim,
  animTick: Tick,
  level: PosInt,
  stars: z.number().int().min(0).max(5),
  /** Платформа атакующего, по которой бьёт. */
  targetSlot: PlatformSlot.nullable(),
});
export type DefenderState = z.infer<typeof DefenderState>;

/**
 * Бой атаки (режим «Карта»). `hq` снимка — база-цель в центре (прочность, вид), `platforms` и `heroes` — платформы и
 * герои атакующего по краю, `zombies` — защитники-зомби (сама база в них не входит).
 */
export const AttackState = z.object({
  kind: z.enum(["zombies", "heroes"]),
  /** Осталось до конца боя, тиков (с начала боя после `intro`), и весь лимит. */
  ticksLeft: UInt,
  ticksTotal: PosInt,
  /** Прочность платформ атакующего (по слотам с героями; нет записи — слот пуст). */
  platformHp: z.array(z.object({ slot: PlatformSlot, hp: UInt, hpMax: PosInt })),
  defenders: z.array(DefenderState),
});
export type AttackState = z.infer<typeof AttackState>;

/** Снимок боя на тик: всё, что нужно нарисовать поле и HUD. */
export const BattleSnapshot = z.object({
  /** Сквозной номер тика с начала боя (не сбрасывается между волнами) — для интерполяции рендера. */
  frame: UInt,
  field: FieldGeometry,
  wave: WaveState,
  hq: HqState,
  /** 6 платформ в обычной игре, в бете до 16 (База со слотами); по `slot` по возрастанию. */
  platforms: z.array(PlatformState).min(6).max(16),
  heroes: z.array(HeroState),
  zombies: z.array(ZombieState),
  projectiles: z.array(ProjectileState),
  effects: z.array(EffectState),
  coins: z.array(CoinDropState),
  /** Посылка на поле: одна или ни одной. */
  parcels: z.array(ParcelState).max(1),
  /** Последняя открытая посылка волны. Не событие: на скорости выше x1 кадр видит только последний из своих тиков. */
  parcelOpened: ParcelOpened.nullable(),
  /** Счётчик монет в шапке: подтверждённые сервером + заработанные в этой волне. */
  wallet: z.object({ coins: Coins, earnedThisWave: Coins }),
  /** «Автонавыки»: открыты с волны 10, включаются игроком. */
  autoSkills: z.object({ unlocked: z.boolean(), enabled: z.boolean() }),
  /** События этого тика (в порядке возникновения). */
  events: z.array(BattleEvent),
  /** Только в бое атаки участка (режим «Карта»). */
  attack: AttackState.optional(),
});
export type BattleSnapshot = z.infer<typeof BattleSnapshot>;
